import { useState } from 'react';
import { AuthAppleSignInBlock } from '../components/AuthAppleSignInBlock';
import { AuthContactPrompt } from '../components/AuthContactSupport';
import { AuthOtpSection } from '../components/AuthOtpSection';
import { AuthPortalPasswordSection } from '../components/AuthPortalPasswordSection';
import { AuthGoogleSignInBlock } from '../components/AuthGoogleSignInBlock';
import { EnvBuildBadge } from '../components/EnvBrandImage';
import { MAIN_PORTAL_LOGIN_TYPE } from '../login/authEntryPolicy';
import { useI18n } from '../i18n';
import styles from './AuthLoginPortal.module.css';

export type AuthLoginPortalProps = {
  onShowOtherSignIn: () => void;
  onShowRegister: () => void;
  onShowAssetOwner: () => void;
  onShowContact: () => void;
  onForgotPassword?: () => void;
};

export function AuthLoginPortal({
  onShowOtherSignIn,
  onShowRegister,
  onShowAssetOwner,
  onShowContact,
  onForgotPassword,
}: AuthLoginPortalProps) {
  const { t } = useI18n();
  const [signInMode, setSignInMode] = useState<'otp' | 'password'>('otp');

  return (
    <div className={styles.portal}>
      <EnvBuildBadge className={styles.envBadge} />

      <h1 className={styles.title}>
        <button
          type="button"
          className={styles.titleSecretButton}
          onClick={onShowOtherSignIn}
          title={t('auth.portal.otherSignInOptions')}
        >
          {t('auth.portal.title')}
        </button>
      </h1>

      <div
        className={styles.otpChannelTabs}
        role="tablist"
        aria-label={t('auth.portal.title')}
      >
        <button
          type="button"
          role="tab"
          className={styles.otpChannelTab}
          aria-selected={signInMode === 'otp'}
          onClick={() => setSignInMode('otp')}
        >
          {t('auth.portal.otpModeTab')}
        </button>
        <button
          type="button"
          role="tab"
          className={styles.otpChannelTab}
          aria-selected={signInMode === 'password'}
          onClick={() => setSignInMode('password')}
        >
          {t('auth.portal.passwordModeTab')}
        </button>
      </div>

      {signInMode === 'otp' ? (
        <AuthOtpSection />
      ) : (
        <AuthPortalPasswordSection onForgotPassword={onForgotPassword} />
      )}

      <div className={styles.divider} role="separator">
        <span>{t('auth.portal.or')}</span>
      </div>

      <div className={styles.socialStack}>
        <AuthGoogleSignInBlock loginType={MAIN_PORTAL_LOGIN_TYPE} />
        <AuthAppleSignInBlock loginType={MAIN_PORTAL_LOGIN_TYPE} />
      </div>

      <footer className={styles.footer}>
        <p className={styles.footerLinksRow}>
          <button type="button" className={styles.footerLink} onClick={onShowRegister}>
            {t('auth.portal.registerLink')}
          </button>
          <span className={styles.footerLinkSep} aria-hidden>
            /
          </span>
          <button type="button" className={styles.footerLink} onClick={onShowAssetOwner}>
            {t('auth.portal.assetOwnerLink')}
          </button>
        </p>
        <AuthContactPrompt onContact={onShowContact} />
      </footer>
    </div>
  );
}
